import $ from "jquery";
import { default as ChartJs } from "chart.js";
import "chartjs-plugin-colorschemes";
import { LabeledComponentDefinition, ValueComponent, ValueComponentDefinition } from "components/components/Component";
import { ValueBaseComponent } from "components/components/ValueBaseComponent";
import { EnumDateOutputType } from "components/output/dateFormats";
import sislogUserGlobals from "components/UserGlobals";
import i18n from "components/i18n/i18n";
import template from "./graph.hbs";
import numbro from "numbro";

export class Graph extends ValueBaseComponent<ChartDefinition> implements ValueComponent {

    public readonly componentType: string = "graph";
    private chart: ChartJs;

    constructor(definition: ChartDefinition) {
        super(definition);
    }

    public render(): Element {

        let jqueryView = $(template(this.definition));
        let canvas = jqueryView.find("canvas");

        this.chart = new ChartJs(canvas, {
            type: this.definition.chartType,
            options: {
                responsive: true,
                plugins: {
                    colorschemes: {
                        scheme: "brewer.Paired12",
                    },
                },
            },
        });

        this.view = jqueryView[0];
        return this.view;
    }

    public value(data: ChartData) {

        this.chart.data.labels.push(...this.applyi18nLabels(data));

        this.chart.data.datasets.forEach((dataset: ChartDataset) => {
            dataset.data.pop();
        });
        data.datasets.forEach((dataset) => {
            this.chart.data.datasets.push(this.transformChartDataset(dataset));
        });

        if (typeof data.aspectRatio !== "undefined") {
            this.chart.aspectRatio = data.aspectRatio;
        }

        if (typeof data.options !== "undefined") {
            this.transformCharOptions(data.options);
        } else {
            //Por defecto, se formatean los datos numericos
            if (this.chart.options.scales.yAxes.length > 0) {
                for (const yAxis of this.chart.options.scales.yAxes) {
                    this.assignNumericFormatAxisTicksCallback(yAxis.ticks, false);
                }
            }
        }

        this.addFormatNumberToLabelPoint();

        this.chart.update();
    }

    private addFormatNumberToLabelPoint() {
        this.chart.options.tooltips = {
            callbacks: {
                label: function (tooltipItem, data) {
                    let newLabel = "";
                    let label = data['datasets'][tooltipItem.datasetIndex]['label'];
                    let value = data['datasets'][tooltipItem.datasetIndex]['data'][tooltipItem['index']];
                    let options: any = {
                        thousandSeparated: true,
                        ...((typeof data['datasets'][tooltipItem.datasetIndex]['isPercentage'] !== "undefined") && (data['datasets'][tooltipItem.datasetIndex]['isPercentage'])) && { output: "percent", spaceSeparated: true },
                        ...(typeof data['datasets'][tooltipItem.datasetIndex]['decimals'] !== "undefined") && { mantissa: data['datasets'][tooltipItem.datasetIndex]['decimals'] }
                    };
                    numbro.setLanguage(sislogUserGlobals.language);
                    let formatValue = numbro(value).format(options);
                    newLabel += label + ': ' + formatValue;

                    return newLabel;
                }
            }
        }
    }
    private transformCharOptions(options: ChartOptions) {

        if (typeof options.title !== "undefined") {
            this.chart.options.title.display = true;
            let text = (i18n.instance.exists(options.title)) ? i18n.instance.t(options.title) : options.title;
            this.chart.options.title.text = text;
        }

        if ((typeof options.scalesXAxes !== "undefined") && (options.scalesXAxes.length > 0)) {
            options.scalesXAxes.forEach((value, index) => {
                this.scalesXAxis(index, value);
            });
        }

        if ((typeof options.scalesYAxes !== "undefined") && (options.scalesYAxes.length > 0)) {
            options.scalesYAxes.forEach((value, index) => {
                this.scalesYAxis(index, value);
            });
        }
    }

    private scalesXAxis(index: number, data: ChartScalesXAxis) {

        let chartXAxis: any = {
            display: true,
            id: data.id,
        };

        if (typeof data.labelString !== "undefined") {
            let text = (i18n.instance.exists(data.labelString)) ? i18n.instance.t(data.labelString) : data.labelString;
            let scaleLabel: any = {
                display: true,
                labelString: text
            };
            chartXAxis.scaleLabel = scaleLabel;
        }

        Object.assign(this.chart.options.scales.xAxes[index], chartXAxis);
    }


    private scalesYAxis(index: number, data: ChartScalesYAxis) {

        let chartYAxis: any = {
            display: true,
            id: data.id,
        };

        if (typeof data.labelString !== "undefined") {
            let text = (i18n.instance.exists(data.labelString)) ? i18n.instance.t(data.labelString) : data.labelString;
            let scaleLabel: any = {
                display: true,
                labelString: text
            };
            chartYAxis.scaleLabel = scaleLabel;
        }

        if (typeof data.ticks !== "undefined") {
            chartYAxis.ticks = data.ticks;
        }

        this.assignNumericFormatAxisTicksCallback(chartYAxis.ticks, ((typeof data.percentage !== "undefined") && data.percentage));

        Object.assign(this.chart.options.scales.yAxes[index], chartYAxis);
    }

    private assignNumericFormatAxisTicksCallback(AxisTicks: any, isPercentage: boolean) {
        Object.assign(AxisTicks, {
            callback: function (value, index, values) {
                let options: any = {
                    thousandSeparated: true,
                    ...(isPercentage) && { output: "percent", spaceSeparated: true }
                };
                numbro.setLanguage(sislogUserGlobals.language);
                return numbro(value).format(options);
            }
        });
    }

    private applyi18nLabels(data: ChartData): Array<string | string[]> {

        let labels = [];

        if ((typeof data.labels !== "undefined") && (data.labels.length > 0)) {
            if ((<any>Object).values(EnumDateOutputType).includes(data.labelsType)) {
                const language = sislogUserGlobals.language;
                data.labels.forEach((item) => {
                    let date = new Date(item);
                    let dateTimezoneOff = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
                    let formatDate = EnumDateOutputType.getIntlDateTimeFormat(EnumDateOutputType.toEnumDateOutputType(data.labelsType)).format(dateTimezoneOff);
                    labels.push(formatDate);
                });

            } else {
                for (const labelKey of data.labels) {
                    if (i18n.instance.exists(labelKey)) {
                        labels.push(i18n.instance.t(labelKey));
                    } else {
                        labels.push(labelKey);
                    }
                }
            }
        }

        return labels;
    }

    private transformChartDataset(dataset: ChartDataset): ChartJs.ChartDataSets {

        let label: string;

        if ((dataset.label != null) && (i18n.instance.exists(dataset.label))) {
            label = i18n.instance.t(dataset.label);
        } else {
            label = dataset.label;
        }

        let objCharDateSet: any = {
            label,
            data: dataset.data,
            ...(typeof dataset.percentage !== "undefined") && { isPercentage: dataset.percentage },
            ...(typeof dataset.decimals !== "undefined") && { decimals: dataset.decimals },
            ...((typeof dataset.xaxisID !== "undefined") && (dataset.xaxisID != null)) && { xaxisID: dataset.xaxisID },
            ...((typeof dataset.yaxisID !== "undefined") && (dataset.yaxisID != null)) && { yaxisID: dataset.yaxisID }
        };

        if (typeof dataset.properties !== "undefined") {
            for (const property in dataset.properties) {
                objCharDateSet[property] = dataset.properties[property];
            }
        }

        return objCharDateSet;
    }

    public getValue() {
        throw new Error("Method not implemented.");
    }
}

enum EnumChartType {
    Bar = "bar",
    Line = "line",
    Pie = "pie",
}

export interface ChartDefinition extends LabeledComponentDefinition, ValueComponentDefinition {
    values: any[];
    chartType: EnumChartType.Bar | EnumChartType.Line | EnumChartType.Pie;
}

export interface ChartData {
    labels?: string[];
    labelsType?: string;
    aspectRatio?: number;
    options?: ChartOptions;
    datasets: ChartDataset[];
}

export interface ChartOptions {
    title?: string;
    scalesXAxes?: ChartScalesXAxis[];
    scalesYAxes?: ChartScalesYAxis[];
}

export interface ChartDataset {
    label?: string;
    data: number[];
    percentage?: boolean;
    xaxisID?: string;
    yaxisID?: string;
    properties?: any;
    decimals?: number;
}

export interface ChartScalesXAxis {
    id: string;
    labelString?: string;
}

export interface ChartScalesYAxis {
    id: string;
    labelString?: string;
    percentage?: boolean;
    ticks?: ChartScalesYAxisTicks;
}

export interface ChartScalesYAxisTicks {
    beginAtZero?: boolean;
}
